'use client';

import { Avatar } from '@/src/shared/ui/avatars/Avatar';
import { cn } from '@/src/shared/lib/utils';
import { useChatStore } from '../model/useChatStore';
import { ClaimedTag } from '@/src/shared/ui/tags/ClaimedTag';

interface MiniChatListProps {
  selectedConversationId: string | null;
  onSelectConversation: (conversationId: string) => void;
  className?: string;
}

/**
 * Mini Chat List - Displays list of conversations in the mini widget
 *
 * Features:
 * - Scrollable list of chats
 * - Avatar, name, task title, last message preview
 * - Unread badge
 * - Time stamp
 * - Claimed tag for active orders
 */
export const MiniChatList = ({
  selectedConversationId,
  onSelectConversation,
  className,
}: MiniChatListProps) => {
  const { conversations, isLoadingConversations } = useChatStore();

  if (isLoadingConversations) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <span className="text-text-secondary">Loading conversations...</span>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8">
        <span className="text-text-secondary text-center">
          No conversations yet
        </span>
        <span className="text-text-secondary text-sm mt-2 text-center">
          Your conversations will appear here
        </span>
      </div>
    );
  }

  return (
    <div className={cn('flex-1 overflow-hidden flex', className)}>
      {/* Chat list */}
      <div className="flex-1 overflow-y-auto">
        {conversations.map((conversation, index) => {
          const isSelected = selectedConversationId === conversation.id;
          const participantName =
            conversation.otherParticipant?.displayName ||
            conversation.otherParticipant?.name ||
            'User';
          const avatarUrl = conversation.otherParticipant?.avatarUrl;
          const lastMessage = conversation.lastMessage?.body || 'No messages yet';
          const time = conversation.lastMessageAt
            ? formatTime(conversation.lastMessageAt)
            : '';
          const unreadCount = conversation.unreadCount || 0;

          // Mock task title - in real app, would come from order data
          const taskTitle = 'Fix the toilet';

          return (
            <div
              key={conversation.id}
              onClick={() => onSelectConversation(conversation.id)}
              className={cn(
                'px-5 py-2.5 cursor-pointer transition-colors relative',
                'flex flex-col gap-5',
                index === 0 ? 'bg-background-secondary' : '',
                index > 0 && 'border-t border-accent-secondary',
                isSelected && 'bg-background-secondary/50',
                !isSelected && 'hover:bg-background-secondary/30'
              )}
            >
              <div className="flex items-center gap-5">
                {/* Avatar */}
                <Avatar
                  src={avatarUrl || undefined}
                  alt={participantName}
                  size="lg"
                  fallback={participantName.charAt(0)}
                  className="w-20 h-20"
                />

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 flex flex-col gap-2.5">
                      {/* Name and unread badge */}
                      <div className="flex items-center gap-2.5">
                        <span className="text-text-secondary text-xl font-medium font-['Roboto'] leading-6">
                          {participantName}
                        </span>
                        {unreadCount > 0 && (
                          <div className="h-6 min-w-6 px-1 bg-feedback-attention rounded-[200px] flex flex-col justify-center items-center">
                            <span className="text-text-inverse text-base font-extrabold font-['Roboto'] leading-5">
                              {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Task title */}
                      <span className="text-text-primary text-xl font-medium font-['Roboto'] leading-6">
                        {taskTitle}
                      </span>

                      {/* Last message preview */}
                      <span className="text-text-primary text-base font-normal font-['Roboto'] leading-5 line-clamp-1">
                        {lastMessage}
                      </span>
                    </div>

                    {/* Time */}
                    <span className="text-text-secondary text-base font-normal font-['Roboto'] leading-5 ml-2 flex-shrink-0">
                      {time}
                    </span>
                  </div>
                </div>
              </div>

              {/* Claimed tag - positioned at bottom right of first item */}
              {index === 1 && (
                <div className="absolute right-5 bottom-3">
                  <ClaimedTag size="sm" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Scrollbar indicator */}
      <div className="w-[5px] py-4 pr-[5px]">
        <div className="w-1 h-20 bg-accent-secondary rounded-[100px]" />
      </div>
    </div>
  );
};

/**
 * Format timestamp for display
 */
function formatTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();

  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  // Today - show time
  if (diffDays === 0) {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  // Yesterday
  if (diffDays === 1) {
    return 'Yesterday';
  }

  // This week - show day name
  if (diffDays < 7) {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  }

  // Older - show date
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}
